<template>
  <div ref="panelRef" class="preview-mobile-qr">
    <button
      type="button"
      class="preview-mobile-qr__trigger"
      :class="{ 'is-active': props.visible }"
      :aria-expanded="props.visible"
      aria-controls="preview-mobile-qr-panel"
      @click.stop="emit('update:visible', !props.visible)"
    >
      <SvgIcon name="book-mobile" :size="16" />
      {{ label }}
    </button>

    <Transition name="preview-mobile-qr__panel">
      <div
        v-if="props.visible"
        id="preview-mobile-qr-panel"
        class="preview-mobile-qr__panel"
        role="dialog"
        aria-label="手机扫码查看"
        @click.stop
      >
        <img class="preview-mobile-qr__image" :src="qrUrl" alt="手机扫码查看" />
        <div class="preview-mobile-qr__label">手机扫码查看</div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, onUnmounted, ref } from 'vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';

  const props = defineProps<{
    visible: boolean;
    label: string;
    qrUrl: string;
  }>();

  const emit = defineEmits<{
    'update:visible': [visible: boolean];
  }>();

  const panelRef = ref<HTMLElement | null>(null);

  const close = () => emit('update:visible', false);

  const handleDocumentPointerdown = (event: PointerEvent) => {
    if (!props.visible) return;
    if (panelRef.value?.contains(event.target as Node)) return;
    close();
  };

  const handleDocumentKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') close();
  };

  onMounted(() => {
    document.addEventListener('pointerdown', handleDocumentPointerdown);
    document.addEventListener('keydown', handleDocumentKeydown);
  });

  onUnmounted(() => {
    document.removeEventListener('pointerdown', handleDocumentPointerdown);
    document.removeEventListener('keydown', handleDocumentKeydown);
  });
</script>

<style scoped>
  .preview-mobile-qr {
    position: relative;
    display: inline-flex;
    user-select: none;
  }

  .preview-mobile-qr__trigger {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 32px;
    padding: 0 16px;
    color: #160211;
    background: rgba(255, 255, 255, 0.15);
    border: 0;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 160ms ease;
  }

  .preview-mobile-qr__trigger:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  .preview-mobile-qr__trigger.is-active {
    background: #fff;
  }

  .preview-mobile-qr__panel {
    position: absolute;
    top: calc(100% + 10px);
    left: 50%;
    z-index: 30;
    width: 196px;
    min-height: 219px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 16px;
    color: #666;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);
    box-sizing: border-box;
    transform: translateX(-50%);
  }

  .preview-mobile-qr__image {
    display: block;
    width: 154px;
    height: 154px;
    object-fit: contain;
  }

  .preview-mobile-qr__label {
    color: #666;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-align: center;
  }

  .preview-mobile-qr__panel-enter-active,
  .preview-mobile-qr__panel-leave-active {
    transition:
      opacity 160ms ease,
      transform 160ms ease;
  }

  .preview-mobile-qr__panel-enter-from,
  .preview-mobile-qr__panel-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-4px);
  }
</style>
